import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BRAND } from '@fin/shared/brand';
import { SERVICE_LABELS, STATUS_COLORS, STATUS_LABELS, type JobStatus } from '@fin/shared/enums';
import { useCustomers, useJobList, useWorkers, useFeedback, type Job } from '../lib/queries';

export function Dashboard() {
  const workers = useWorkers();
  const customers = useCustomers();
  const scheduled = useJobList('scheduled');
  const assigned = useJobList('assigned');
  const accepted = useJobList('accepted');
  const onTheWay = useJobList('on_my_way');
  const inProgress = useJobList('in_progress');
  const completed = useJobList('completed');
  const cancelled = useJobList('cancelled');
  const onHold = useJobList('on_hold');
  const feedback = useFeedback();

  const allJobs: Job[] = useMemo(
    () => [
      ...(scheduled.data ?? []),
      ...(assigned.data ?? []),
      ...(accepted.data ?? []),
      ...(onTheWay.data ?? []),
      ...(inProgress.data ?? []),
      ...(completed.data ?? []),
      ...(cancelled.data ?? []),
      ...(onHold.data ?? []),
    ],
    [scheduled.data, assigned.data, accepted.data, onTheWay.data, inProgress.data, completed.data, cancelled.data, onHold.data],
  );

  const activeNow =
    (assigned.data?.length ?? 0) +
    (accepted.data?.length ?? 0) +
    (onTheWay.data?.length ?? 0) +
    (inProgress.data?.length ?? 0);

  const todayDubai = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' });
  const isToday = (iso: string) =>
    new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' }) === todayDubai;

  const completedToday = (completed.data ?? []).filter((j) => isToday(j.updatedAt)).length;

  const trend = useMemo(() => buildTrend(completed.data ?? [], 7), [completed.data]);

  const statusBreakdown: { status: JobStatus; count: number }[] = [
    { status: 'scheduled', count: scheduled.data?.length ?? 0 },
    { status: 'assigned', count: assigned.data?.length ?? 0 },
    { status: 'accepted', count: accepted.data?.length ?? 0 },
    { status: 'on_my_way', count: onTheWay.data?.length ?? 0 },
    { status: 'in_progress', count: inProgress.data?.length ?? 0 },
    { status: 'on_hold', count: onHold.data?.length ?? 0 },
  ];
  const pipelineTotal = statusBreakdown.reduce((s, x) => s + x.count, 0);

  const topServices = useMemo(() => {
    const counts = new Map<string, number>();
    for (const j of allJobs) counts.set(j.serviceType, (counts.get(j.serviceType) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [allJobs]);

  const ratings = (feedback.data ?? []).map((f) => f.rating).filter((r): r is number => typeof r === 'number');
  const avgRating = ratings.length ? ratings.reduce((s, r) => s + r, 0) / ratings.length : 0;

  const completedCount = completed.data?.length ?? 0;
  const cancelledCount = cancelled.data?.length ?? 0;
  const finishedTotal = completedCount + cancelledCount;
  const completionRate = finishedTotal > 0 ? (completedCount / finishedTotal) * 100 : 0;

  const cards: StatCardProps[] = [
    {
      to: '/workers',
      label: 'Workers',
      value: workers.data?.length,
      loading: workers.isPending,
      from: '#0F2D1F',
      to2: '#1F8A5F',
      icon: <IconUsers />,
      sub: 'Active team members',
    },
    {
      to: '/customers',
      label: 'Customers',
      value: customers.data?.length,
      loading: customers.isPending,
      from: '#1E3A8A',
      to2: '#3B82F6',
      icon: <IconAddressBook />,
      sub: 'Total customers',
    },
    {
      to: '/today',
      label: 'Active now',
      value: activeNow,
      loading: assigned.isPending || onTheWay.isPending || inProgress.isPending,
      from: '#92400E',
      to2: '#FBBF24',
      icon: <IconBolt />,
      sub: 'Jobs in flight',
      pulse: true,
    },
    {
      to: '/today',
      label: 'Completed today',
      value: completedToday,
      loading: completed.isPending,
      from: '#065F46',
      to2: '#10B981',
      icon: <IconCheck />,
      sub: `${completedCount} all-time`,
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold" style={{ color: BRAND.primary }}>Dashboard</h1>
        <p className="text-sm text-stone-500">
          Operations at a glance · {new Date().toLocaleDateString('en-AE', { timeZone: 'Asia/Dubai', dateStyle: 'full' })}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c, i) => <StatCard key={c.label} {...c} delay={i * 60} />)}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border bg-white p-5 shadow-sm lg:col-span-2" style={{ borderColor: BRAND.border }}>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-stone-700">Last 7 days · completed</h2>
              <p className="text-xs text-stone-500">Daily throughput</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold" style={{ color: '#10B981' }}>
                {trend.reduce((s, t) => s + t.count, 0)}
              </p>
              <p className="text-[10px] uppercase tracking-wide text-stone-500">last 7 days</p>
            </div>
          </div>
          <BarChart data={trend} />
        </div>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-1">
          <MiniStat
            label="Completion rate"
            value={`${completionRate.toFixed(0)}%`}
            sub={`${completedCount} done · ${cancelledCount} cancelled`}
            color="#10B981"
            icon={<IconTrendUp />}
          />
          <MiniStat
            label="Avg rating"
            value={avgRating ? avgRating.toFixed(1) : '—'}
            sub={`${ratings.length} review${ratings.length === 1 ? '' : 's'}`}
            color="#F59E0B"
            icon={<IconStar />}
          />
          <MiniStat
            label="Jobs / worker"
            value={workers.data?.length ? (allJobs.length / workers.data.length).toFixed(1) : '—'}
            sub="Avg lifetime load"
            color="#3B82F6"
            icon={<IconLayers />}
          />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border bg-white p-5 shadow-sm" style={{ borderColor: BRAND.border }}>
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-stone-700">Active pipeline</h2>
              <p className="text-xs text-stone-500">Jobs not yet completed or cancelled</p>
            </div>
            <Link to="/jobs" className="text-xs font-medium hover:underline" style={{ color: BRAND.primary }}>View all →</Link>
          </div>
          {pipelineTotal === 0 ? (
            <p className="py-8 text-center text-sm text-stone-500">No active jobs in the pipeline.</p>
          ) : (
            <>
              <StackedBar segments={statusBreakdown.map((s) => ({ value: s.count, color: STATUS_COLORS[s.status] }))} total={pipelineTotal} />
              <ul className="mt-4 space-y-2">
                {statusBreakdown.filter((s) => s.count > 0).map((s) => (
                  <li key={s.status} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[s.status] }} />
                      <span className="text-stone-700">{STATUS_LABELS[s.status]?.en ?? s.status}</span>
                    </span>
                    <span className="tabular-nums text-stone-600">
                      <span className="font-semibold">{s.count}</span>
                      <span className="ml-2 text-xs text-stone-400">{((s.count / pipelineTotal) * 100).toFixed(0)}%</span>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="rounded-2xl border bg-white p-5 shadow-sm" style={{ borderColor: BRAND.border }}>
          <div className="mb-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-stone-700">Top services</h2>
            <p className="text-xs text-stone-500">By total job volume</p>
          </div>
          {topServices.length === 0 ? (
            <p className="py-8 text-center text-sm text-stone-500">No service data yet.</p>
          ) : (
            <ul className="space-y-3">
              {topServices.map(([svc, count]) => {
                const max = topServices[0]?.[1] ?? 1;
                const pct = (count / max) * 100;
                const label = SERVICE_LABELS[svc as keyof typeof SERVICE_LABELS]?.en ?? svc;
                return (
                  <li key={svc}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="font-medium text-stone-700">{label}</span>
                      <span className="tabular-nums text-stone-600">{count}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundImage: `linear-gradient(90deg, ${BRAND.primary}, ${BRAND.primarySoft ?? '#1F8A5F'})` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {(feedback.data?.length ?? 0) > 0 && (
        <div className="mt-6 rounded-2xl border bg-white p-5 shadow-sm" style={{ borderColor: BRAND.border }}>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-stone-700">Recent feedback</h2>
            <Link to="/feedback" className="text-xs font-medium hover:underline" style={{ color: BRAND.primary }}>View all →</Link>
          </div>
          <ul className="space-y-2">
            {(feedback.data ?? []).slice(0, 3).map((f, i) => (
              <li key={`${f.jobId}-${i}`} className="flex items-start justify-between gap-3 rounded-lg border border-stone-100 p-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-stone-800">
                    {f.customerName ?? 'Customer'}{' '}
                    <span className="text-xs font-normal text-stone-500">· {new Date(f.createdAt).toLocaleDateString('en-AE')}</span>
                  </p>
                  {f.comment && <p className="mt-0.5 line-clamp-1 text-xs text-stone-600">{f.comment}</p>}
                </div>
                {typeof f.rating === 'number' && (
                  <span className="shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                    ★ {f.rating.toFixed(1)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function buildTrend(jobs: Job[], days: number): { day: string; count: number }[] {
  const now = new Date();
  const out: { day: string; count: number }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' });
    const label = d.toLocaleDateString('en-AE', { timeZone: 'Asia/Dubai', weekday: 'short' });
    const count = jobs.filter((j) => new Date(j.updatedAt).toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' }) === key).length;
    out.push({ day: label, count });
  }
  return out;
}

function BarChart({ data }: { data: { day: string; count: number }[] }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="flex h-40 items-end gap-2">
      {data.map((d, i) => {
        const h = (d.count / max) * 100;
        return (
          <div key={i} className="flex flex-1 flex-col items-center gap-1">
            <div className="relative w-full flex-1">
              <div
                className="absolute bottom-0 w-full rounded-t-md transition-all"
                style={{
                  height: `${h}%`,
                  minHeight: d.count > 0 ? '4px' : '0',
                  backgroundImage: `linear-gradient(180deg, #34D399 0%, #10B981 100%)`,
                }}
                title={`${d.count} completed`}
              />
              {d.count > 0 && (
                <span
                  className="absolute left-1/2 -translate-x-1/2 text-[10px] font-semibold tabular-nums text-stone-700"
                  style={{ bottom: `calc(${h}% + 4px)` }}
                >
                  {d.count}
                </span>
              )}
            </div>
            <span className="text-[10px] uppercase text-stone-500">{d.day}</span>
          </div>
        );
      })}
    </div>
  );
}

function StackedBar({ segments, total }: { segments: { value: number; color: string }[]; total: number }) {
  return (
    <div className="flex h-3 w-full overflow-hidden rounded-full bg-stone-100">
      {segments.map((s, i) =>
        s.value > 0 ? <div key={i} style={{ width: `${(s.value / total) * 100}%`, backgroundColor: s.color }} /> : null,
      )}
    </div>
  );
}

function MiniStat({ label, value, sub, color, icon }: { label: string; value: string; sub: string; color: string; icon: ReactNode }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border bg-white p-4 shadow-sm" style={{ borderColor: BRAND.border }}>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white" style={{ backgroundColor: color }}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</p>
        <p className="text-xl font-bold leading-tight tabular-nums" style={{ color: BRAND.primary }}>{value}</p>
        <p className="truncate text-[11px] text-stone-500">{sub}</p>
      </div>
    </div>
  );
}

interface StatCardProps {
  to: string;
  label: string;
  value: number | undefined;
  loading: boolean;
  from: string;
  to2: string;
  icon: ReactNode;
  sub: string;
  pulse?: boolean;
  delay?: number;
}

function StatCard({ to, label, value, loading, from, to2, icon, sub, pulse, delay = 0 }: StatCardProps) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  const display = useCountUp(value ?? 0, 900);

  return (
    <Link
      to={to}
      className="group relative block overflow-hidden rounded-2xl shadow-sm transition-all duration-500 ease-out hover:-translate-y-1 hover:shadow-2xl"
      style={{
        background: `linear-gradient(135deg, ${from} 0%, ${to2} 100%)`,
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0)' : 'translateY(12px)',
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full opacity-20 blur-2xl transition-all duration-500 group-hover:scale-125 group-hover:opacity-30"
        style={{ background: 'white' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-12 -left-8 h-32 w-32 rounded-full opacity-10 blur-2xl"
        style={{ background: 'white' }}
      />
      <div className="relative p-5">
        <div className="flex items-start justify-between">
          <div
            className="flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-sm transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110"
            style={{ background: 'rgba(255,255,255,0.20)' }}
          >
            {icon}
          </div>
          {pulse && !loading && (value ?? 0) > 0 && (
            <span className="relative inline-flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
            </span>
          )}
        </div>
        <p className="mt-5 text-xs font-medium uppercase tracking-wider text-white/80">{label}</p>
        <p className="mt-1 text-4xl font-bold tabular-nums text-white drop-shadow-sm">
          {loading ? <span className="inline-block animate-pulse">···</span> : display}
        </p>
        <div className="mt-3 flex items-center justify-between text-xs text-white/80">
          <span>{sub}</span>
          <span className="inline-flex items-center gap-1 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            View
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </span>
        </div>
      </div>
    </Link>
  );
}

function useCountUp(target: number, durationMs: number): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (target <= 0) { setN(0); return; }
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return n;
}

const iconProps = {
  width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
  strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};
function IconUsers() {
  return (
    <svg {...iconProps}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function IconAddressBook() {
  return (
    <svg {...iconProps}>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <circle cx="12" cy="11" r="3" />
      <path d="M7 19c1-2.5 3-3.5 5-3.5s4 1 5 3.5" />
    </svg>
  );
}
function IconBolt() { return (<svg {...iconProps}><path d="M13 2 3 14h7l-1 8 10-12h-7z" /></svg>); }
function IconCheck() {
  return (
    <svg {...iconProps}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <path d="m9 11 3 3L22 4" />
    </svg>
  );
}
function IconStar() {
  return (
    <svg {...iconProps} width={18} height={18}>
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />
    </svg>
  );
}
function IconTrendUp() {
  return (
    <svg {...iconProps} width={18} height={18}>
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  );
}
function IconLayers() {
  return (
    <svg {...iconProps} width={18} height={18}>
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}
